// Frame sizes and per-frame compositing for fixed-frame screen sharing. Kept free of any DOM or Worker setup so the
// same code runs on the page (fallback) and inside fixed-frame.worker.ts.

// Every Rise Vision template is 1920x1080 (1080x1920 on portrait displays).
export const FIXED_FRAME_WIDTH = 1920;
export const FIXED_FRAME_HEIGHT = 1080;

export type FrameOrientation = 'landscape' | 'portrait';

export function getFixedFrameSize(orientation: FrameOrientation): {
  width: number;
  height: number;
} {
  return orientation === 'portrait'
    ? { width: FIXED_FRAME_HEIGHT, height: FIXED_FRAME_WIDTH }
    : { width: FIXED_FRAME_WIDTH, height: FIXED_FRAME_HEIGHT };
}

export interface FrameRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Scales the source to fill the frame on at least one axis (up or down), centered, keeping its aspect ratio. */
export function computeLetterbox(
  sourceWidth: number,
  sourceHeight: number,
  frameWidth = FIXED_FRAME_WIDTH,
  frameHeight = FIXED_FRAME_HEIGHT,
): FrameRect {
  if (sourceWidth <= 0 || sourceHeight <= 0) {
    return { x: 0, y: 0, width: frameWidth, height: frameHeight };
  }

  const scale = Math.min(frameWidth / sourceWidth, frameHeight / sourceHeight);
  // Even sizes keep the 4:2:0 chroma planes aligned and avoid a 1px seam.
  const width = Math.min(frameWidth, Math.round((sourceWidth * scale) / 2) * 2);
  const height = Math.min(
    frameHeight,
    Math.round((sourceHeight * scale) / 2) * 2,
  );

  return {
    x: Math.floor((frameWidth - width) / 2),
    y: Math.floor((frameHeight - height) / 2),
    width,
    height,
  };
}

// A static screen produces no captured frames, and unlike the raw capture track, the generated track can't be asked to
// repeat its last one. A display that connects then gets no keyframe and keeps asking for a new connection, so the
// last frame is re-sent when nothing new arrived for this long.
// Every 100ms rather than every second: a display's hardware decoder can hold about 2 frames before showing one
// (Amazon signage sticks do), so on a static screen a slide change stayed hidden until 2 more keepalives arrived, over 2s
// at 1 per second. Identical frames also encode far smaller at 10 per second than 1 whole bitrate-worth each second.
const KEEPALIVE_FRAME_INTERVAL_MS = 100;
const KEEPALIVE_CHECK_INTERVAL_MS = 250;
// A new encoder drops about the next 100 frames after its first keyframe (Chrome's VideoToolbox encoder on macOS),
// counted in frames, not time: 3s of motion, but over 100s of 1s keepalives, during which the display stays black. So
// after a refresh, i.e. a connection coming up, the last frame is re-sent at a normal frame rate for a few seconds.
const REFRESH_BURST_FRAME_INTERVAL_MS = 33;
const REFRESH_BURST_DURATION_MS = 5000;
// The screen counts as still once nothing was captured for this long, so short pauses in motion (a video
// buffering, a slow scroll) don't flip it back and forth.
const STILL_AFTER_MS = 1000;

/**
 * Draws each captured frame, letterboxed, onto a fixed-size black canvas and emits that canvas as the frame. While no
 * frame arrives, re-emits the last one every `keepaliveMs`. `onRefresh` receives a function that re-emits it right away,
 * e.g. so a new connection gets a frame without waiting for the next keepalive, and then keeps re-emitting it every
 * `burstFrameMs` for `burstMs` while no new frame arrives. `onStillChange` is called with true once nothing was
 * captured for a while, and with false when a frame arrives after that, each time with how many frames were captured
 * so far. `onRedraw` receives a function that draws a picture of the screen in place of the last frame and re-emits it,
 * unless a frame was captured after the count it's given.
 */
export function createLetterboxTransform(
  frameWidth: number,
  frameHeight: number,
  keepaliveMs = KEEPALIVE_FRAME_INTERVAL_MS,
  onRefresh?: (refresh: () => void) => void,
  burstMs = REFRESH_BURST_DURATION_MS,
  burstFrameMs = REFRESH_BURST_FRAME_INTERVAL_MS,
  onStillChange?: (still: boolean, capturedFrames: number) => void,
  onRedraw?: (
    redraw: (image: ImageBitmap, capturedFrames: number) => void,
  ) => void,
): TransformStream<VideoFrame, VideoFrame> {
  const canvas = new OffscreenCanvas(frameWidth, frameHeight);
  const context = canvas.getContext('2d', { alpha: false });
  if (!context) {
    throw new Error('OffscreenCanvas 2D context unavailable');
  }

  // Timestamps are in microseconds and must keep increasing: WebRTC drops a frame that isn't newer than the last one.
  let lastTimestamp: number | null = null;
  let lastEmittedAt = 0;
  let keepaliveTimer: ReturnType<typeof setInterval> | null = null;
  let burstTimer: ReturnType<typeof setInterval> | null = null;
  let burstUntil = 0;
  let lastCapturedAt: number | null = null;
  let capturedFrames = 0;
  let still = false;
  const drawLetterboxed = (
    image: CanvasImageSource,
    width: number,
    height: number,
  ) => {
    const rect = computeLetterbox(width, height, frameWidth, frameHeight);
    context.fillStyle = '#000';
    context.fillRect(0, 0, frameWidth, frameHeight);
    context.drawImage(image, rect.x, rect.y, rect.width, rect.height);
  };
  const stopBurst = () => {
    if (burstTimer !== null) {
      clearInterval(burstTimer);
      burstTimer = null;
    }
  };
  const stopKeepalive = () => {
    if (keepaliveTimer !== null) {
      clearInterval(keepaliveTimer);
      keepaliveTimer = null;
    }
    stopBurst();
  };

  // cancel runs when the consumer goes away, where flush doesn't. It isn't in this TypeScript version's Transformer type.
  const transformer: Transformer<VideoFrame, VideoFrame> & {
    cancel: () => void;
  } = {
    start: (controller) => {
      const resendLastFrame = () => {
        // Stopped, nothing drawn yet, or the consumer hasn't taken the last one.
        if (
          keepaliveTimer === null ||
          lastTimestamp === null ||
          (controller.desiredSize ?? 0) < 0
        ) {
          return;
        }
        const now = performance.now();
        // At least a millisecond on: WebRTC compares frame times in milliseconds.
        const timestamp =
          lastTimestamp +
          Math.max(1000, Math.round((now - lastEmittedAt) * 1000));
        let keepaliveFrame: VideoFrame;
        try {
          keepaliveFrame = new VideoFrame(canvas, { timestamp });
        } catch (error) {
          // Re-sending is best effort: keep the stream, which real frames can still use, and stop retrying.
          console.warn(
            'Fixed-frame keepalive stopped: frame creation failed.',
            error,
          );
          stopKeepalive();
          return;
        }
        try {
          controller.enqueue(keepaliveFrame);
        } catch {
          // The stream closed or errored; the pipe reports that itself.
          keepaliveFrame.close();
          stopKeepalive();
          return;
        }
        lastTimestamp = timestamp;
        lastEmittedAt = now;
      };
      keepaliveTimer = setInterval(
        () => {
          const now = performance.now();
          if (
            !still &&
            lastCapturedAt !== null &&
            now - lastCapturedAt >= STILL_AFTER_MS
          ) {
            still = true;
            onStillChange?.(true, capturedFrames);
          }
          if (now - lastEmittedAt >= keepaliveMs) {
            resendLastFrame();
          }
        },
        // A quarter of the interval: a check landing just short of it waits one more check, so checking only once per
        // interval sent frames every 1-2 intervals (6-7 a second instead of 10).
        Math.min(KEEPALIVE_CHECK_INTERVAL_MS, keepaliveMs / 4),
      );
      const refresh = () => {
        if (keepaliveTimer === null) {
          return;
        }
        resendLastFrame();
        // A refresh during a burst extends it.
        burstUntil = performance.now() + burstMs;
        if (burstTimer === null && burstMs > 0) {
          burstTimer = setInterval(() => {
            const now = performance.now();
            if (now >= burstUntil) {
              stopBurst();
            } else if (now - lastEmittedAt >= burstFrameMs) {
              resendLastFrame();
            }
          }, burstFrameMs);
        }
      };
      onRefresh?.(refresh);
      // A picture taken after the screen went still replaces the last frame only if nothing was captured since: a
      // newer captured frame is at least as current.
      const redraw = (image: ImageBitmap, expectedFrames: number) => {
        try {
          if (keepaliveTimer === null || capturedFrames !== expectedFrames) {
            return;
          }
          drawLetterboxed(image, image.width, image.height);
        } finally {
          image.close();
        }
        resendLastFrame();
      };
      onRedraw?.(redraw);
    },
    transform: (frame, controller) => {
      lastCapturedAt = performance.now();
      capturedFrames++;
      if (still) {
        still = false;
        onStillChange?.(false, capturedFrames);
      }
      try {
        drawLetterboxed(frame, frame.displayWidth, frame.displayHeight);
        const timestamp =
          lastTimestamp === null
            ? frame.timestamp
            : Math.max(frame.timestamp, lastTimestamp + 1);
        controller.enqueue(
          new VideoFrame(canvas, {
            timestamp,
            duration: frame.duration ?? undefined,
          }),
        );
        lastTimestamp = timestamp;
        lastEmittedAt = performance.now();
      } catch (error) {
        stopKeepalive();
        throw error;
      } finally {
        frame.close();
      }
    },
    flush: () => stopKeepalive(),
    cancel: () => stopKeepalive(),
  };
  return new TransformStream<VideoFrame, VideoFrame>(transformer);
}
